import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Autocomplete,
  CircularProgress as AutocompleteLoading,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CalculateIcon from '@mui/icons-material/Calculate';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import SubjectIcon from '@mui/icons-material/Subject';
import ShieldIcon from '@mui/icons-material/Shield';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PageHeader from '../components/common/PageHeader';
import SandboxFreigabeToggle from '../components/common/SandboxFreigabeToggle';
import DiskussionContextLink from '../components/diskussion/DiskussionContextLink';
import FormSkeleton from '../components/common/FormSkeleton';
import ConfirmDialog from '../components/common/ConfirmDialog';
import HistorieTimeline from '../components/common/HistorieTimeline';
import VersionsHistorie from '../components/common/VersionsHistorie';
import CvssCalculator, {
  parseVectorString,
  BASE_METRICS,
  TEMPORAL_METRICS,
} from '../components/common/CvssCalculator';
import TipTapEditor, { TipTapEditorHandle, EditImageHandler, EditImageResult } from '../components/common/TipTapEditor';
import { parseWordImageSize, type WordFindingLeadChrome, type WordImageSize } from '../components/common/wordPageLayout';
import ScreenshotGallery from '../components/common/ScreenshotGallery';
import ImageAnnotationEditor from '../components/common/image-editor/ImageAnnotationEditor';
import { bumpImageVersion } from '../utils/imageVersionStore';
import BurpRequestResponseSection from '../components/common/BurpRequestResponseSection';
import BountyVerteilung from '../components/common/BountyVerteilung';
import { MitarbeiterKurzprofilLink } from '../components/common/MitarbeiterKurzprofilLink';
import KommentarSection from '../components/schwachstelle/KommentarSection';
import DetailbeschreibungAIAssistantDialog from '../components/schwachstelle/DetailbeschreibungAIAssistantDialog';
import { filesService } from '../services/filesService';
import { burpService } from '../services/burpService';
import { schwachstellenService } from '../services/schwachstellenService';
import { auditsService } from '../services/auditsService';
import { mitarbeiterService } from '../services/mitarbeiterService';
import {
  schwachstellenklassenService,
  toolsService,
  schwachstellenStatusService,
  risikoszenarienService,
  cweService,
} from '../services/lookupService';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useNotification } from '../hooks/NotificationContext';
import { useSchwachstelleLock, useLockViewerPolling } from '../hooks/useSchwachstelleLock';
import { MITARBEITER_VIEW, SCHWACHSTELLEN_DELETE, SCHWACHSTELLEN_DELETE_OWN, SCHWACHSTELLEN_REVIEW, SCHWACHSTELLEN_SUBMIT, VERWALTUNG_MANAGE } from '../constants/permissions';
import {
  Schwachstelle,
  SchwachstelleCreate,
  SchwachstelleList,
  SchwachstelleLockInfo,
  AuditList,
  MitarbeiterList,
  FileEntity,
  Tool,
  Schwachstellenklasse,
  SchwachstellenStatus,
  Risikoszenario,
  CweEntry,
} from '../types';
import { format } from 'date-fns';
import { formatSeverityLower, getCvssSeverityLabelLower, getKritikalitaetHexColor } from '../utils/severity';
import { formatSchwachstellenStatusDisplay } from '../utils/schwachstellenStatus';
import { parseUtcDateTime } from '../utils/time';
import { validateIpAdresse } from '../utils/ipAdresse';
import { extractFieldError } from '../utils/errorUtils';
import { findingKlasseName, findingRefTargets } from '../utils/schwachstelleReferenz';

const SchwachstelleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { showSuccess, showError } = useNotification();
  const isNew = id === 'neu';
  /** TipTap-Toolbar: unter fixer AppBar + sticky Aktionsleiste (vgl. top der Leiste). */
  const tiptapStickyToolbarTop = { xs: 108, sm: 116 };

  const [schwachstelle, setSchwachstelle] = useState<Schwachstelle | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cvssDialogOpen, setCvssDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [historieOpen, setHistorieOpen] = useState(false);
  const [versionenOpen, setVersionenOpen] = useState(false);
  const [lockInfo, setLockInfo] = useState<SchwachstelleLockInfo | null>(null);
  const [kommentarRefresh, setKommentarRefresh] = useState(0);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [rejectTargetStatusId, setRejectTargetStatusId] = useState<number | null>(null);
  const [detailAiDialogOpen, setDetailAiDialogOpen] = useState(false);

  const schwachstelleId = isNew ? null : (id ? parseInt(id) : null);
  const autoEditAttempted = useRef(false);

  // Editor refs for image insertion
  const allgemeinEditorRef = useRef<TipTapEditorHandle>(null);
  const detailEditorRef = useRef<TipTapEditorHandle>(null);
  const mitigationEditorRef = useRef<TipTapEditorHandle>(null);
  const activeEditorRef = useRef<TipTapEditorHandle | null>(null);
  const [galleryImages, setGalleryImages] = useState<FileEntity[]>([]);

  const handleInsertImage = useCallback((fileId: number, wordSize?: WordImageSize) => {
    const url = filesService.getPreviewUrl(fileId);
    const size = parseWordImageSize(wordSize);
    if (activeEditorRef.current) {
      activeEditorRef.current.insertImage(url, undefined, size);
    } else if (detailEditorRef.current) {
      detailEditorRef.current.insertImage(url, undefined, size);
    }
  }, []);

  const handleInsertCodeBlock = useCallback(async (fileId: number) => {
    try {
      const content = await filesService.getFileContent(fileId);
      if (activeEditorRef.current) {
        activeEditorRef.current.insertCodeBlock(content);
      } else if (detailEditorRef.current) {
        detailEditorRef.current.insertCodeBlock(content);
      }
    } catch {
      // Error shown by global axios interceptor
    }
  }, []);

  const handleInsertBurpCodeBlock = useCallback(async (pairId: number) => {
    try {
      const content = await burpService.getCombinedContent(pairId);
      if (activeEditorRef.current) {
        activeEditorRef.current.insertCodeBlock(content);
      } else if (detailEditorRef.current) {
        detailEditorRef.current.insertCodeBlock(content);
      }
    } catch {
      // Error shown by global axios interceptor
    }
  }, []);

  const isImageFile = useCallback((filename: string): boolean => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext);
  }, []);

  const handleScreenshotFilesLoaded = useCallback((files: FileEntity[]) => {
    setGalleryImages(files.filter((file) => isImageFile(file.dateiname)));
  }, [isImageFile]);

  // ── Bild-Annotations-Editor (Galerie, Inline-Bilder, Fokus-Seitenleiste) ──
  const galleryImagesRef = useRef<FileEntity[]>([]);
  useEffect(() => {
    galleryImagesRef.current = galleryImages;
  }, [galleryImages]);
  const [imageEditorState, setImageEditorState] = useState<{
    file: FileEntity;
    files: FileEntity[];
    canInsert: boolean;
    wordSize?: WordImageSize;
  } | null>(null);
  const imageEditorResolveRef = useRef<((result: EditImageResult) => void) | null>(null);
  const [galleryRefreshToken, setGalleryRefreshToken] = useState(0);

  const handleEditImage: EditImageHandler = useCallback(
    async (fileId, { allowInsert, wordSize }) => {
      let files = galleryImagesRef.current;
      let file = files.find((f) => f.id === fileId);
      try {
        if (!file) {
          file = await filesService.get(fileId);
          files = [...files, file];
        }
        if (file.original_file_id && !files.some((f) => f.id === file!.original_file_id)) {
          try {
            files = [...files, await filesService.get(file.original_file_id)];
          } catch {
            // Original nicht mehr vorhanden: Editor startet mit der bearbeiteten Datei als neuer Basis
          }
        }
      } catch {
        showError('Bild konnte nicht geladen werden');
        return null;
      }
      return new Promise<EditImageResult>((resolve) => {
        imageEditorResolveRef.current = resolve;
        setImageEditorState({ file: file!, files, canInsert: allowInsert, wordSize });
      });
    },
    [showError],
  );

  const finishImageEdit = useCallback((result: EditImageResult) => {
    imageEditorResolveRef.current?.(result);
    imageEditorResolveRef.current = null;
    setImageEditorState(null);
  }, []);

  const handleImageEditorSaved = useCallback(
    (saved: FileEntity, options: { insert: boolean; createdNew: boolean; wordSize: WordImageSize }) => {
      bumpImageVersion(saved.id);
      setGalleryImages((prev) =>
        options.createdNew ? [...prev, saved] : prev.map((f) => (f.id === saved.id ? saved : f)),
      );
      setGalleryRefreshToken((t) => t + 1);
      if (options.insert) handleInsertImage(saved.id, options.wordSize);
      showSuccess(
        options.createdNew
          ? options.insert
            ? 'Bearbeitete Bildversion gespeichert und eingefügt'
            : 'Bearbeitete Bildversion gespeichert – das Original bleibt erhalten'
          : 'Bildbearbeitung aktualisiert',
      );
      finishImageEdit({ fileId: saved.id, inserted: options.insert, wordSize: options.wordSize });
    },
    [finishImageEdit, handleInsertImage, showSuccess],
  );

  const handlePasteImageUpload = useCallback(
    async (file: File): Promise<string | null> => {
      if (!schwachstelleId) return null;
      try {
        const ent = await filesService.upload(file, undefined, undefined, undefined, schwachstelleId);
        if (isImageFile(ent.dateiname)) {
          setGalleryImages((prev) => [...prev, ent]);
        }
        return filesService.getPreviewUrl(ent.id);
      } catch {
        return null;
      }
    },
    [schwachstelleId, isImageFile],
  );

  // Lock management
  const [lockLost, setLockLost] = useState(false);
  const { releaseLock, expiresAt: lockExpiresAt, setInitialExpiresAt } = useSchwachstelleLock({
    schwachstelleId,
    enabled: editing && !isNew,
    onLockLost: () => {
      setLockLost(true);
      setEditing(false);
    },
  });

  // Viewer-side polling: auto-detect when a foreign lock is released
  useLockViewerPolling({
    schwachstelleId,
    lockInfo,
    currentUserId: user?.id,
    onLockReleased: () => setLockInfo(null),
  });

  // Live countdown ticker (re-renders every second while a lock is active)
  const [, setTick] = useState(0);
  useEffect(() => {
    const hasRelevantLock = (editing && !isNew && lockExpiresAt) || (lockInfo && lockInfo.locked_by.id !== user?.id);
    if (!hasRelevantLock) return;
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, [editing, isNew, lockExpiresAt, lockInfo, user?.id]);

  const isAdmin = hasPermission(VERWALTUNG_MANAGE);
  const canUseDetailbeschreibungAi = Boolean(user?.bleeding_edge);

  // Form state
  const [formData, setFormData] = useState<SchwachstelleCreate>({
    titel: '',
    detailbeschreibung: '',
    allgemeine_beschreibung: '',
    kritikalitaet: '',
    cvss31_score: undefined,
    cvss31_vector: undefined,
    host: '',
    ip_adresse: '',
    mitigationsmassnahmen: '',
    audit_id: 0,
    schwachstellenklasse_id: undefined,
    cwe_id: undefined,
    reporter_id: undefined,
    tool_ids: [],
    risikoszenario_ids: [],
  });

  // Lookup data
  const [audits, setAudits] = useState<AuditList[]>([]);
  const [mitarbeiter, setMitarbeiter] = useState<MitarbeiterList[]>([]);
  const [schwachstellenklassen, setSchwachstellenklassen] = useState<Schwachstellenklasse[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [risikoszenarien, setRisikoszenarien] = useState<Risikoszenario[]>([]);
  const [statuses, setStatuses] = useState<SchwachstellenStatus[]>([]);
  const [cweOptions, setCweOptions] = useState<CweEntry[]>([]);
  const [cweSearchText, setCweSearchText] = useState('');
  const [cweLoading, setCweLoading] = useState(false);
  const [selectedCwe, setSelectedCwe] = useState<CweEntry | null>(null);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [hostSuggestions, setHostSuggestions] = useState<string[]>([]);
  const [ipSuggestions, setIpSuggestions] = useState<string[]>([]);
  const [ipAdresseError, setIpAdresseError] = useState<string | null>(null);
  const [auditFindings, setAuditFindings] = useState<SchwachstelleList[]>([]);

  const sortByLabel = useCallback((a: string, b: string) => a.localeCompare(b, 'de', { sensitivity: 'base' }), []);
  const sortedSchwachstellenklassen = useMemo(
    () => [...schwachstellenklassen].sort((a, b) => sortByLabel(a.name_de, b.name_de)),
    [schwachstellenklassen, sortByLabel]
  );
  const sortedTools = useMemo(
    () => [...tools].sort((a, b) => sortByLabel(a.name, b.name)),
    [tools, sortByLabel]
  );
  const sortedRisikoszenarien = useMemo(
    () => [...risikoszenarien].sort((a, b) => sortByLabel(a.name_de, b.name_de)),
    [risikoszenarien, sortByLabel]
  );

  useEffect(() => {
    const auditIdParam = searchParams.get('audit_id');
    if (isNew && auditIdParam) {
      setFormData((prev) => ({ ...prev, audit_id: parseInt(auditIdParam) }));
    }
  }, [searchParams, isNew]);

  useEffect(() => {
    if (!formData.audit_id) {
      setAuditFindings([]);
      setHostSuggestions([]);
      setIpSuggestions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await schwachstellenService.list({
          audit_id: formData.audit_id,
          limit: 500,
        });
        if (cancelled) return;
        setAuditFindings(rows);
        const others = rows.filter((r) => r.id !== schwachstelleId);
        const hosts = Array.from(
          new Set(
            others
              .map((r) => r.host?.trim())
              .filter((s): s is string => Boolean(s))
          )
        ).sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }));
        const ips = Array.from(
          new Set(
            others
              .map((r) => r.ip_adresse?.trim())
              .filter((s): s is string => Boolean(s))
          )
        ).sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }));
        if (!cancelled) {
          setHostSuggestions(hosts);
          setIpSuggestions(ips);
        }
      } catch {
        if (!cancelled) {
          setAuditFindings([]);
          setHostSuggestions([]);
          setIpSuggestions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.audit_id, schwachstelleId]);

  const canViewMitarbeiter = hasPermission(MITARBEITER_VIEW);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        setLoadingLookups(true);
        const [auditsData, klassenData, toolsData, risikoszenarienData, statusData] =
          await Promise.all([
            auditsService.list(),
            schwachstellenklassenService.list(),
            toolsService.list(),
            risikoszenarienService.list(),
            schwachstellenStatusService.list(),
          ]);
        setAudits(auditsData);
        setSchwachstellenklassen(klassenData);
        setTools(toolsData);
        setRisikoszenarien(risikoszenarienData);
        setStatuses(statusData);

        if (canViewMitarbeiter) {
          try {
            const mitarbeiterData = await mitarbeiterService.list();
            setMitarbeiter(mitarbeiterData);
          } catch {
            // Reviewer/other roles without mitarbeiter:view - gracefully degrade
          }
        }
      } catch (error) {
        console.error('Fehler beim Laden der Lookup-Daten:', error);
      } finally {
        setLoadingLookups(false);
      }
    };

    loadLookups();
  }, [canViewMitarbeiter]);

  useEffect(() => {
    if (!isNew && id) {
      const loadSchwachstelle = async () => {
        try {
          setLoading(true);
          const data = await schwachstellenService.get(parseInt(id));
          setSchwachstelle(data);
          setLockInfo(data.lock_info || null);
          setFormData({
            titel: data.titel,
            detailbeschreibung: data.detailbeschreibung || '',
            allgemeine_beschreibung: data.allgemeine_beschreibung || '',
            kritikalitaet: data.kritikalitaet || '',
            cvss31_score: data.cvss31_score,
            cvss31_vector: data.cvss31_vector || undefined,
            host: data.host || '',
            ip_adresse: data.ip_adresse || '',
            mitigationsmassnahmen: data.mitigationsmassnahmen || '',
            audit_id: data.audit_id,
            schwachstellenklasse_id: data.schwachstellenklasse_id,
            cwe_id: data.cwe_id,
            reporter_id: data.reporter_id,
            tool_ids: data.tools.map((t) => t.id),
            risikoszenario_ids: data.risikoszenarien.map((r) => r.id),
          });
          if (data.cwe) {
            setSelectedCwe(data.cwe);
          }
        } catch (error) {
          console.error('Fehler beim Laden der Schwachstelle:', error);
        } finally {
          setLoading(false);
        }
      };

      loadSchwachstelle();
    }
  }, [id, isNew]);

  const handleInputChange = (field: keyof SchwachstelleCreate, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'ip_adresse') {
      setIpAdresseError(validateIpAdresse(typeof value === 'string' ? value : ''));
    }
  };

  const handleSchwachstellenklasseChange = (klasseId: number | undefined) => {
    setFormData((prev) => ({ ...prev, schwachstellenklasse_id: klasseId }));
  };

  const handleInsertKlasseBeschreibung = (lang: 'de' | 'en') => {
    const klasse = schwachstellenklassen.find((k) => k.id === formData.schwachstellenklasse_id);
    if (!klasse) return;
    const text = lang === 'de' ? klasse.allgemeine_beschreibung_de : klasse.allgemeine_beschreibung_en;
    if (!text) return;
    handleInputChange('allgemeine_beschreibung', text);
  };

  // CWE search with debounce
  useEffect(() => {
    if (!cweSearchText || cweSearchText.length < 1) {
      return;
    }
    const timer = setTimeout(async () => {
      setCweLoading(true);
      try {
        const results = await cweService.search(cweSearchText, 30);
        setCweOptions(results);
      } catch (err) {
        console.error('CWE-Suche fehlgeschlagen:', err);
      } finally {
        setCweLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [cweSearchText]);

  const handleStartEditing = async () => {
    if (!isNew && schwachstelleId) {
      try {
        const lockRes = await schwachstellenService.lock(schwachstelleId);
        setLockInfo(null);
        setLockLost(false);
        setInitialExpiresAt(lockRes.expires_at);
        setEditing(true);
      } catch {
        // Error shown by global axios interceptor
      }
    } else {
      setEditing(true);
    }
  };

  useEffect(() => {
    if (autoEditAttempted.current || isNew || loading || !schwachstelle || !schwachstelleId) {
      return;
    }

    const editParam = searchParams.get('edit');
    if (!editParam || !['1', 'true', 'yes'].includes(editParam.toLowerCase())) {
      return;
    }

    autoEditAttempted.current = true;
    navigate({ search: '' }, { replace: true });

    const lockedByOther = !!lockInfo && lockInfo.locked_by.id !== user?.id;
    if (editing || lockedByOther) {
      return;
    }

    void handleStartEditing();
  }, [
    loading,
    schwachstelle,
    isNew,
    searchParams,
    editing,
    lockInfo,
    user?.id,
    schwachstelleId,
    navigate,
  ]);

  const handleForceUnlock = async () => {
    if (!schwachstelleId) return;
    try {
      await schwachstellenService.forceUnlock(schwachstelleId);
      setLockInfo(null);
      showSuccess('Lock wurde aufgehoben');
    } catch {
      // Error shown by global axios interceptor
    }
  };

  const handleCancelEditing = async () => {
    if (schwachstelle) {
      setFormData({
        titel: schwachstelle.titel,
        detailbeschreibung: schwachstelle.detailbeschreibung || '',
        allgemeine_beschreibung: schwachstelle.allgemeine_beschreibung || '',
        kritikalitaet: schwachstelle.kritikalitaet || '',
        cvss31_score: schwachstelle.cvss31_score,
        cvss31_vector: schwachstelle.cvss31_vector || undefined,
        host: schwachstelle.host || '',
        ip_adresse: schwachstelle.ip_adresse || '',
        mitigationsmassnahmen: schwachstelle.mitigationsmassnahmen || '',
        audit_id: schwachstelle.audit_id,
        schwachstellenklasse_id: schwachstelle.schwachstellenklasse_id,
        reporter_id: schwachstelle.reporter_id,
        tool_ids: schwachstelle.tools.map((t) => t.id),
        risikoszenario_ids: schwachstelle.risikoszenarien.map((r) => r.id),
      });
    }
    setIpAdresseError(null);
    setEditing(false);
    await releaseLock();
    setLockInfo(null);
  };

  const handleSave = async () => {
    const ipError = validateIpAdresse(formData.ip_adresse);
    if (ipError) {
      setIpAdresseError(ipError);
      showError(ipError);
      return;
    }
    try {
      setSaving(true);
      if (isNew) {
        await schwachstellenService.create(formData);
        showSuccess('Schwachstelle erfolgreich erstellt');
        const auditIdParam = searchParams.get('audit_id');
        setTimeout(() => {
          if (auditIdParam) {
            navigate(`/audits/${auditIdParam}`);
          } else {
            navigate('/audits');
          }
        }, 1000);
      } else if (id) {
        const updated = await schwachstellenService.update(parseInt(id), formData);
        setSchwachstelle(updated);
        showSuccess('Schwachstelle erfolgreich aktualisiert');
        setEditing(false);
        await releaseLock();
        setLockInfo(null);
      }
    } catch (error) {
      const fieldError = extractFieldError(error, 'ip_adresse');
      if (fieldError) setIpAdresseError(fieldError);
      // Weitere Fehler zeigt der globale Axios-Interceptor
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || isNew) return;

    try {
      const auditId = formData.audit_id;
      await schwachstellenService.delete(parseInt(id));
      showSuccess('Schwachstelle erfolgreich gelöscht');
      setDeleteDialogOpen(false);
      setTimeout(() => {
        if (auditId) {
          navigate(`/audits/${auditId}`);
        } else {
          navigate('/schwachstellen');
        }
      }, 1000);
    } catch {
      setDeleteDialogOpen(false);
      // Error shown by global axios interceptor
    }
  };

  const handleCvssApply = (score: number, vector: string) => {
    setFormData((prev) => ({ ...prev, cvss31_score: score, cvss31_vector: vector }));
    setCvssDialogOpen(false);
  };

  const getCvssSeverityColor = (score?: number): string => {
    if (score === undefined || score === null) return '#78909c';
    if (score === 0) return '#78909c';
    if (score <= 3.9) return '#4caf50';
    if (score <= 6.9) return '#ff9800';
    if (score <= 8.9) return '#f44336';
    return '#9c27b0';
  };

  const getKritikalitaetColor = (krit?: string): string => getKritikalitaetHexColor(krit);

  const getStatusColor = (statusName?: string): string => {
    switch (statusName) {
      case 'Geprueft':
        return '#4caf50';
      case 'Review erbeten':
        return '#ff9800';
      case 'Abgelehnt':
        return '#b71c1c';
      case 'Entwurf':
      default:
        return '#2196f3';
    }
  };

  const handleStatusTransition = async (targetStatusId: number, kommentar?: string) => {
    if (!id || isNew) return;
    try {
      setReviewing(true);
      const updated = await schwachstellenService.statusTransition(parseInt(id), targetStatusId, kommentar);
      setSchwachstelle(updated);
      setKommentarRefresh((prev) => prev + 1);
      showSuccess('Status erfolgreich aktualisiert');
    } catch {
      // Error shown by global axios interceptor
    } finally {
      setReviewing(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectTargetStatusId || !rejectComment.trim()) return;
    await handleStatusTransition(rejectTargetStatusId, rejectComment.trim());
    setRejectDialogOpen(false);
    setRejectComment('');
    setRejectTargetStatusId(null);
  };

  const handleGenerateReport = async () => {
    if (!id || isNew) return;
    try {
      setGenerating(true);
      await schwachstellenService.generateReport(parseInt(id));
    } catch {
      // Error shown by global axios interceptor
    } finally {
      setGenerating(false);
    }
  };

  const currentStatusName = schwachstelle?.status?.name || 'Entwurf';
  const isReporter = schwachstelle?.reporter_id === user?.id;

  const canSubmitForReview =
    !isNew
    && !!schwachstelle
    && (currentStatusName === 'Entwurf' || currentStatusName === 'Abgelehnt')
    && (
      (isReporter && hasPermission(SCHWACHSTELLEN_SUBMIT))
      || hasPermission(SCHWACHSTELLEN_REVIEW)
    );

  const canReview =
    !isNew && schwachstelle && !isReporter && currentStatusName === 'Review erbeten'
    && hasPermission(SCHWACHSTELLEN_REVIEW);

  const canDelete =
    !isNew &&
    (hasPermission(SCHWACHSTELLEN_DELETE) ||
      (hasPermission(SCHWACHSTELLEN_DELETE_OWN) && formData.reporter_id === user?.id));

  const isLockedByOther = !!lockInfo && lockInfo.locked_by.id !== user?.id;

  /* ── Lookup helpers ─────────────────────────────────────────── */
  const getAuditName = (aid: number) => audits.find((a) => a.id === aid)?.name;
  const getKlasseName = (kid?: number) => schwachstellenklassen.find((k) => k.id === kid)?.name_de;
  const getReporterName = (rid?: number) => {
    const m = mitarbeiter.find((m) => m.id === rid);
    if (m) return `${m.vorname} ${m.nachname}`;
    if (schwachstelle?.reporter && schwachstelle.reporter_id === rid) {
      return `${schwachstelle.reporter.vorname} ${schwachstelle.reporter.nachname}`;
    }
    return undefined;
  };
  const getToolNames = (ids?: number[]) =>
    (ids || [])
      .map((tid) => tools.find((t) => t.id === tid)?.name || String(tid))
      .sort((a, b) => sortByLabel(a, b));
  const getRisikoszenarioNames = (ids?: number[]) =>
    (ids || [])
      .map((rid) => risikoszenarien.find((r) => r.id === rid)?.name_de || String(rid))
      .sort((a, b) => sortByLabel(a, b));

  const detailbeschreibungAiContext = useMemo(() => ({
    schwachstelle_id: schwachstelleId,
    titel: formData.titel || '',
    detailbeschreibung: formData.detailbeschreibung || '',
    allgemeine_beschreibung: formData.allgemeine_beschreibung || '',
    kritikalitaet: formData.kritikalitaet || '',
    cvss31_score: formData.cvss31_score,
    cvss31_vector: formData.cvss31_vector || '',
    host: formData.host || '',
    ip_adresse: formData.ip_adresse || '',
    schwachstellenklasse: getKlasseName(formData.schwachstellenklasse_id) || '',
    cwe: selectedCwe
      ? `CWE-${selectedCwe.id}: ${selectedCwe.name}`
      : schwachstelle?.cwe
        ? `CWE-${schwachstelle.cwe.id}: ${schwachstelle.cwe.name}`
        : '',
    tools: getToolNames(formData.tool_ids),
    risikoszenarien: getRisikoszenarioNames(formData.risikoszenario_ids),
  }), [
    formData,
    getKlasseName,
    getRisikoszenarioNames,
    getToolNames,
    schwachstelle,
    schwachstelleId,
    selectedCwe,
  ]);

  const findingReferenceLang: 'de' | 'en' =
    audits.find((item) => item.id === formData.audit_id)?.sprache === 'en' ? 'en' : 'de';
  const findingReferences = useMemo(
    () => findingRefTargets(auditFindings.map((row) => ({
      id: row.id,
      titel: row.titel,
      klasse: findingKlasseName(row.schwachstellenklasse, findingReferenceLang),
    }))),
    [auditFindings, findingReferenceLang],
  );

  const wordFindingLead = useMemo<WordFindingLeadChrome>(() => {
    const audit = audits.find((a) => a.id === formData.audit_id);
    const lang: WordFindingLeadChrome['lang'] = audit?.sprache === 'en' ? 'en' : 'de';
    const klasse = schwachstellenklassen.find((k) => k.id === formData.schwachstellenklasse_id);
    const klasseName =
      (lang === 'en' && klasse?.name_en ? klasse.name_en : klasse?.name_de) || '';
    const title = [klasseName, formData.titel].filter(Boolean).join(' \u2013 ');
    const vector = (formData.cvss31_vector || '').replace(/^CVSS:3\.1\//i, '').trim();
    const score = formData.cvss31_score != null ? String(formData.cvss31_score) : '';
    const cwe = selectedCwe
      ? `CWE-${selectedCwe.id}: ${selectedCwe.name}`
      : schwachstelle?.cwe
        ? `CWE-${schwachstelle.cwe.id}: ${schwachstelle.cwe.name}`
        : '';
    const sevDe = formatSeverityLower(formData.kritikalitaet);
    const sevEn: Record<string, string> = {
      kritisch: 'critical',
      hoch: 'high',
      mittel: 'medium',
      niedrig: 'low',
      info: 'informational',
    };
    const prefix = (audit?.projektkennung || 'SV').trim() || 'SV';
    const seq = schwachstelle?.sort_order && schwachstelle.sort_order > 0 ? schwachstelle.sort_order : 1;
    return {
      lang,
      // Erstes Finding im Audit-Report trägt zusätzlich die Kapitelzeile „Schwachstellen“.
      isFirstInReport: seq === 1,
      title,
      referenzId: `${prefix}-${String(seq).padStart(3, '0')}`,
      host: formData.ip_adresse || '',
      domain: formData.host || '',
      criticality: lang === 'en' ? sevEn[sevDe] || sevDe : sevDe,
      cvss: [vector, score].filter(Boolean).join(' '),
      cwe,
      // Wie fill_schwachstelle_template: Report-Sprache, Reihenfolge wie gespeichert (nicht sortiert).
      risks: (formData.risikoszenario_ids || [])
        .map((rid) => {
          const rs = risikoszenarien.find((r) => r.id === rid);
          return (lang === 'en' && rs?.name_en ? rs.name_en : rs?.name_de) || '';
        })
        .filter(Boolean)
        .join(', '),
      tools: (formData.tool_ids || [])
        .map((tid) => tools.find((t) => t.id === tid)?.name || '')
        .filter(Boolean)
        .join(', '),
      commonHtml: formData.allgemeine_beschreibung || '',
    };
  }, [
    audits,
    formData.audit_id,
    formData.titel,
    formData.schwachstellenklasse_id,
    formData.cvss31_score,
    formData.cvss31_vector,
    formData.host,
    formData.ip_adresse,
    formData.kritikalitaet,
    formData.allgemeine_beschreibung,
    formData.risikoszenario_ids,
    formData.tool_ids,
    risikoszenarien,
    tools,
    schwachstelle?.cwe,
    schwachstelle?.sort_order,
    schwachstellenklassen,
    selectedCwe,
  ]);

  // ── QA Quick Checks ──────────────────────────────────
  const hasContent = (html?: string): boolean => {
    if (!html) return false;
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text.length > 0;
  };

  const qaChecks = useMemo(() => {
    const headerMissing: string[] = [];
    if (!formData.titel?.trim()) headerMissing.push('Titel');
    if (!formData.kritikalitaet) headerMissing.push('Kritikalität');
    if (formData.cvss31_score == null) headerMissing.push('CVSS Score');
    if (!formData.cvss31_vector) headerMissing.push('CVSS Vector');
    if (!formData.schwachstellenklasse_id) headerMissing.push('Schwachstellenklasse');
    if (!formData.cwe_id) headerMissing.push('CWE');
    if (!formData.host?.trim()) headerMissing.push('Host');
    if (!formData.ip_adresse?.trim()) headerMissing.push('IP-Adresse');
    if (!formData.tool_ids?.length) headerMissing.push('Tools');
    if (!formData.risikoszenario_ids?.length) headerMissing.push('Risikoszenarien');

    const headerOk = headerMissing.length === 0;
    const allgemeinOk = hasContent(formData.allgemeine_beschreibung);
    const detailOk = hasContent(formData.detailbeschreibung);
    const mitigationOk = hasContent(formData.mitigationsmassnahmen);

    let screenshotsOk = true;
    const unusedScreenshots: string[] = [];
    if (galleryImages.length > 0) {
      const combined = [
        formData.allgemeine_beschreibung || '',
        formData.detailbeschreibung || '',
        formData.mitigationsmassnahmen || '',
      ].join(' ');
      for (const img of galleryImages) {
        if (!combined.includes(`/files/${img.id}/preview`)) {
          screenshotsOk = false;
          unusedScreenshots.push(img.dateiname);
        }
      }
    }

    return {
      header: { ok: headerOk, tooltip: headerOk ? 'Header vollständig' : `Fehlend: ${headerMissing.join(', ')}` },
      allgemein: { ok: allgemeinOk, tooltip: allgemeinOk ? 'Allgemeine Beschreibung vorhanden' : 'Allgemeine Beschreibung fehlt' },
      detail: { ok: detailOk, tooltip: detailOk ? 'Detailbeschreibung vorhanden' : 'Detailbeschreibung fehlt' },
      mitigation: { ok: mitigationOk, tooltip: mitigationOk ? 'Mitigationsmaßnahmen vorhanden' : 'Mitigationsmaßnahmen fehlen' },
      screenshots: {
        ok: screenshotsOk,
        tooltip: screenshotsOk
          ? (galleryImages.length === 0 ? 'Keine Screenshots vorhanden' : 'Alle Screenshots in Benutzung')
          : `Nicht referenziert: ${unusedScreenshots.join(', ')}`,
      },
    };
  }, [formData, galleryImages]);

  if (loading || loadingLookups) {
    return <FormSkeleton fields={10} />;
  }

  /* ── Metadata Table Styles ─────────────────────────────────── */
  const labelCellSx = {
    fontWeight: 600,
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    bgcolor: isDark ? alpha(theme.palette.background.paper, 0.6) : alpha(theme.palette.grey[100], 0.8),
    border: `1px solid ${isDark ? alpha(theme.palette.divider, 0.2) : theme.palette.divider}`,
    width: '160px',
    py: 1,
    px: 1.5,
    whiteSpace: 'nowrap' as const,
  };

  const valueCellSx = {
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
    border: `1px solid ${isDark ? alpha(theme.palette.divider, 0.2) : theme.palette.divider}`,
    py: 1,
    px: 1.5,
  };

  /* ── Workflow buttons for the sticky bar ─────────────────── */
  const renderWorkflowButtons = () => {
    if (isNew || !schwachstelle) return null;

    const buttons: React.ReactNode[] = [];

    if (canSubmitForReview) {
      const reviewErbetenStatus = statuses.find((s) => s.name === 'Review erbeten');
      if (reviewErbetenStatus) {
        buttons.push(
          <Button
            key="submit-review"
            size="small"
            variant="contained"
            disabled={reviewing}
            startIcon={reviewing ? <CircularProgress size={14} /> : <RateReviewIcon />}
            onClick={() => handleStatusTransition(reviewErbetenStatus.id)}
            sx={{
              bgcolor: '#ff9800',
              color: '#fff',
              '&:hover': { bgcolor: '#f57c00' },
            }}
          >
            Zur Prüfung
          </Button>
        );
      }
    }

    if (canReview) {
      const geprueftStatus = statuses.find((s) => s.name === 'Geprueft');
      const abgelehntStatus = statuses.find((s) => s.name === 'Abgelehnt');
      if (geprueftStatus) {
        buttons.push(
          <Button
            key="approve"
            size="small"
            variant="contained"
            disabled={reviewing}
            startIcon={reviewing ? <CircularProgress size={14} /> : <RateReviewIcon />}
            onClick={() => handleStatusTransition(geprueftStatus.id)}
            sx={{
              bgcolor: '#4caf50',
              color: '#fff',
              '&:hover': { bgcolor: '#388e3c' },
            }}
          >
            Genehmigen
          </Button>
        );
      }
      if (abgelehntStatus) {
        buttons.push(
          <Button
            key="reject"
            size="small"
            variant="outlined"
            disabled={reviewing}
            startIcon={reviewing ? <CircularProgress size={14} /> : <RateReviewIcon />}
            onClick={() => {
              setRejectTargetStatusId(abgelehntStatus.id);
              setRejectDialogOpen(true);
            }}
            sx={{
              borderColor: '#f4433680',
              color: '#f44336',
              '&:hover': {
                bgcolor: '#f4433620',
                borderColor: '#f44336',
              },
            }}
          >
            Ablehnen
          </Button>
        );
      }
    }

    return buttons.length > 0 ? buttons : null;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PageHeader
            title={isNew ? 'Neue Schwachstelle' : schwachstelle?.titel || 'Schwachstelle'}
            breadcrumbs={[
              { label: 'Audits', path: '/audits' },
              ...(formData.audit_id
                ? [{ label: getAuditName(formData.audit_id) || 'Audit', path: `/audits/${formData.audit_id}` }]
                : []),
              { label: isNew ? 'Neu' : schwachstelle?.titel || 'Schwachstelle' },
            ]}
          />
          {!isNew && id && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <DiskussionContextLink
                anchor={{ refType: 'schwachstelle', refId: parseInt(id, 10) }}
                objectLabel={schwachstelle?.titel}
              />
              <SandboxFreigabeToggle kind="schwachstelle" id={parseInt(id, 10)} auditId={formData.audit_id || null} />
            </Box>
          )}
        </Box>

        {/* QA Quick Checks + Status/Reporter/Reviewer – same height */}
        {!isNew && schwachstelle && (
          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2, flexShrink: 0, mt: 0.5 }}>
            <Card sx={{ display: 'flex' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                  QA-Checks
                </Typography>
                <Divider orientation="vertical" flexItem />
                {[
                  { key: 'header', icon: <AssignmentIcon sx={{ fontSize: 18 }} />, label: 'Header', check: qaChecks.header },
                  { key: 'allgemein', icon: <DescriptionIcon sx={{ fontSize: 18 }} />, label: 'Allgemein', check: qaChecks.allgemein },
                  { key: 'detail', icon: <SubjectIcon sx={{ fontSize: 18 }} />, label: 'Detail', check: qaChecks.detail },
                  { key: 'mitigation', icon: <ShieldIcon sx={{ fontSize: 18 }} />, label: 'Mitigation', check: qaChecks.mitigation },
                  { key: 'screenshots', icon: <PhotoLibraryIcon sx={{ fontSize: 18 }} />, label: 'Screenshots', check: qaChecks.screenshots },
                ].map(({ key, icon, label, check }) => (
                  <Tooltip key={key} title={check.tooltip} arrow>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, cursor: 'default' }}>
                      <Box sx={{ color: check.ok ? '#4caf50' : '#f44336', display: 'flex' }}>
                        {icon}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.6rem',
                          lineHeight: 1,
                          color: check.ok ? '#4caf50' : '#f44336',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  </Tooltip>
                ))}
              </CardContent>
            </Card>

            <Card sx={{ display: 'flex' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Status */}
              <Chip
                label={formatSchwachstellenStatusDisplay(schwachstelle.status?.name)}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  bgcolor: `${getStatusColor(schwachstelle.status?.name)}20`,
                  color: getStatusColor(schwachstelle.status?.name),
                  border: `1px solid ${getStatusColor(schwachstelle.status?.name)}40`,
                }}
              />

              <Divider orientation="vertical" flexItem />

              {/* Reporter */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <PersonIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, display: 'block', lineHeight: 1.2 }}>
                    Reporter
                  </Typography>
                  {editing ? (
                    <Select
                      variant="standard"
                      size="small"
                      value={formData.reporter_id || ''}
                      onChange={(e) => handleInputChange('reporter_id', e.target.value || undefined)}
                      disableUnderline
                      displayEmpty
                      sx={{ fontSize: '0.875rem', fontWeight: 500, '& .MuiSelect-select': { py: 0 } }}
                    >
                      <MenuItem value="">
                        <em>Keine Auswahl</em>
                      </MenuItem>
                      {mitarbeiter.map((m) => (
                        <MenuItem key={m.id} value={m.id}>
                          {m.vorname} {m.nachname}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Typography variant="body2" component="div" sx={{ fontWeight: 500, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                      {formData.reporter_id ? (
                        <MitarbeiterKurzprofilLink mitarbeiterId={formData.reporter_id} sx={{ fontWeight: 500 }}>
                          {getReporterName(formData.reporter_id) ?? `Mitarbeiter #${formData.reporter_id}`}
                        </MitarbeiterKurzprofilLink>
                      ) : (
                        '–'
                      )}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem />

              {/* Reviewer */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <RateReviewIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, display: 'block', lineHeight: 1.2 }}>
                    Reviewer
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography variant="body2" component="div" sx={{ fontWeight: 500, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                      {(schwachstelle.reviewed_by_id ?? schwachstelle.reviewed_by?.id) ? (
                        <MitarbeiterKurzprofilLink
                          mitarbeiterId={schwachstelle.reviewed_by_id ?? schwachstelle.reviewed_by!.id}
                          sx={{ fontWeight: 500 }}
                        >
                          {schwachstelle.reviewed_by
                            ? `${schwachstelle.reviewed_by.vorname} ${schwachstelle.reviewed_by.nachname}`
                            : `Mitarbeiter #${schwachstelle.reviewed_by_id ?? schwachstelle.reviewed_by!.id}`}
                        </MitarbeiterKurzprofilLink>
                      ) : (
                        '–'
                      )}
                    </Typography>
                    {schwachstelle.reviewed_at && (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                        ({format(new Date(schwachstelle.reviewed_at), 'dd.MM.yyyy HH:mm')})
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      {/* ── Sticky Action Bar ──────────────────────────────── */}
      <Box
        sx={{
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: theme.zIndex.appBar - 1,
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          mb: 3,
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          boxShadow: isDark
            ? `0 2px 8px ${alpha('#000', 0.3)}`
            : `0 2px 8px ${alpha('#000', 0.08)}`,
        }}
      >
        {/* Left side: primary actions */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {editing ? (
            <>
              <Button
                data-testid="btn-save"
                size="small"
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || !formData.titel || !formData.audit_id}
              >
                Speichern
              </Button>
              {!isNew && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={handleCancelEditing}
                >
                  Abbrechen
                </Button>
              )}
            </>
          ) : (
            <>
              <Tooltip title={isLockedByOther ? `Wird bearbeitet von ${lockInfo!.locked_by.vorname} ${lockInfo!.locked_by.nachname}` : 'Bearbeiten'}>
                <span>
                  <Button
                    data-testid="btn-edit"
                    size="small"
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleStartEditing}
                    disabled={isLockedByOther}
                  >
                    Bearbeiten
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                startIcon={generating ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                onClick={handleGenerateReport}
                disabled={generating}
              >
                Report
              </Button>
              {canDelete && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Löschen
                </Button>
              )}
            </>
          )}

          {/* Workflow buttons */}
          {!editing && renderWorkflowButtons()}
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Right side: status, lock info, history buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Lock indicator: locked by another user (with countdown + force-unlock) */}
          {isLockedByOther && lockInfo && (() => {
            const remaining = Math.max(0, Math.floor((parseUtcDateTime(lockInfo.expires_at).getTime() - Date.now()) / 1000));
            const mm = Math.floor(remaining / 60);
            const ss = remaining % 60;
            return (
              <>
                <Chip
                  icon={<LockIcon />}
                  label={`Gesperrt von ${lockInfo.locked_by.vorname} ${lockInfo.locked_by.nachname} (${mm}:${String(ss).padStart(2, '0')})`}
                  size="small"
                  sx={{
                    bgcolor: alpha('#f44336', 0.1),
                    color: '#f44336',
                    border: `1px solid ${alpha('#f44336', 0.3)}`,
                    fontWeight: 600,
                  }}
                />
                {isAdmin && (
                  <Tooltip title="Lock aufheben (Admin)">
                    <IconButton size="small" onClick={handleForceUnlock}
                      sx={{ color: '#f44336', '&:hover': { bgcolor: alpha('#f44336', 0.1) } }}>
                      <LockOpenIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            );
          })()}

          {/* Lock indicator: locked by current user (with countdown) */}
          {editing && !isNew && (() => {
            const exp = lockExpiresAt || null;
            if (!exp) return (
              <Chip icon={<LockIcon />} label="Gesperrt" size="small"
                sx={{ bgcolor: alpha('#ff9800', 0.1), color: '#ff9800', border: `1px solid ${alpha('#ff9800', 0.3)}`, fontWeight: 600 }} />
            );
            const remaining = Math.max(0, Math.floor((parseUtcDateTime(exp).getTime() - Date.now()) / 1000));
            const mm = Math.floor(remaining / 60);
            const ss = remaining % 60;
            return (
              <Chip icon={<LockIcon />}
                label={`Gesperrt (${mm}:${String(ss).padStart(2, '0')})`}
                size="small"
                sx={{ bgcolor: alpha('#ff9800', 0.1), color: '#ff9800', border: `1px solid ${alpha('#ff9800', 0.3)}`, fontWeight: 600 }} />
            );
          })()}

          {!isNew && id && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Tooltip title="Änderungshistorie">
                <IconButton size="small" onClick={() => setHistorieOpen(true)}>
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Versionshistorie">
                <IconButton size="small" onClick={() => setVersionenOpen(true)}>
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* Lock-lost inline banner */}
      {lockLost && (
        <Alert
          severity="error"
          variant="filled"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => { setLockLost(false); handleStartEditing(); }}>
              Erneut sperren
            </Button>
          }
          onClose={() => setLockLost(false)}
        >
          Lock verloren — ein anderer Benutzer hat den Lock übernommen. Ihre Änderungen wurden nicht gespeichert.
        </Alert>
      )}

      {/* Status info alerts */}
      {!editing && !isNew && schwachstelle && (
        <>
          {isReporter && currentStatusName === 'Review erbeten' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Diese Schwachstelle wartet auf Review durch einen Reviewer.
            </Alert>
          )}
          {isReporter && currentStatusName === 'Abgelehnt' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Die Schwachstelle wurde abgelehnt. Bitte die Hinweise in der Zeitleiste beachten und nach Überarbeitung erneut zur Prüfung einreichen.
            </Alert>
          )}
          {isReporter && currentStatusName === 'Geprueft' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Diese Schwachstelle wurde geprüft und genehmigt.
            </Alert>
          )}
        </>
      )}

      {/* ── Metadata Table (unified: inline-editable) ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Table size="small">
            <TableBody>
              {/* Titel – only visible in edit mode */}
              {editing && (
                <TableRow>
                  <TableCell sx={labelCellSx}>Titel</TableCell>
                  <TableCell sx={valueCellSx}>
                    <TextField
                      data-testid="field-titel"
                      variant="standard"
                      size="small"
                      fullWidth
                      value={formData.titel}
                      onChange={(e) => handleInputChange('titel', e.target.value)}
                      required
                      InputProps={{ disableUnderline: true }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.9rem', p: 0 } }}
                    />
                  </TableCell>
                </TableRow>
              )}

              {/* Kritikalitaet */}
              <TableRow>
                <TableCell sx={labelCellSx}>Kritikalitaet</TableCell>
                <TableCell sx={valueCellSx}>
                  {editing ? (
                    <Select
                      data-testid="field-kritikalitaet"
                      variant="standard"
                      size="small"
                      value={formData.kritikalitaet}
                      onChange={(e) => handleInputChange('kritikalitaet', e.target.value)}
                      displayEmpty
                      disableUnderline
                      sx={{ fontSize: '0.9rem', '& .MuiSelect-select': { py: 0 } }}
                    >
                      <MenuItem value="">
                        <em>Keine Auswahl</em>
                      </MenuItem>
                      <MenuItem value="Kritisch">kritisch</MenuItem>
                      <MenuItem value="Hoch">hoch</MenuItem>
                      <MenuItem value="Mittel">mittel</MenuItem>
                      <MenuItem value="Niedrig">niedrig</MenuItem>
                      <MenuItem value="Info">info</MenuItem>
                    </Select>
                  ) : formData.kritikalitaet ? (
                    <Chip
                      label={formatSeverityLower(formData.kritikalitaet)}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: `${getKritikalitaetColor(formData.kritikalitaet)}20`,
                        color: getKritikalitaetColor(formData.kritikalitaet),
                        border: `1px solid ${getKritikalitaetColor(formData.kritikalitaet)}40`,
                      }}
                    />
                  ) : (
                    '–'
                  )}
                </TableCell>
              </TableRow>

              {/* CVSS 3.1 */}
              <TableRow>
                <TableCell sx={labelCellSx}>CVSS 3.1</TableCell>
                <TableCell sx={valueCellSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {formData.cvss31_vector && (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {formData.cvss31_vector}
                      </Typography>
                    )}
                    {formData.cvss31_score != null && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                          {formData.cvss31_score.toFixed(1)}
                        </Typography>
                        <Chip
                          label={getCvssSeverityLabelLower(formData.cvss31_score)}
                          size="small"
                          sx={{
                            bgcolor: getCvssSeverityColor(formData.cvss31_score),
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      </>
                    )}
                    {!formData.cvss31_vector && formData.cvss31_score == null && !editing && '–'}
                    {!editing && formData.cvss31_vector && (() => {
                      const parsed = parseVectorString(formData.cvss31_vector);
                      const allMetrics = [...BASE_METRICS, ...TEMPORAL_METRICS];
                      const entries = allMetrics
                        .filter((def) => parsed[def.abbr] && parsed[def.abbr] !== 'X')
                        .map((def) => {
                          const opt = def.options.find((o) => o.abbr === parsed[def.abbr]);
                          return { name: def.name, abbr: def.abbr, value: parsed[def.abbr], label: opt?.label, tooltip: opt?.tooltip };
                        });
                      if (entries.length === 0) return null;
                      return (
                        <Tooltip
                          arrow
                          title={
                            <Box sx={{ p: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: '0.8rem' }}>
                                CVSS 3.1 Vektor-Details
                              </Typography>
                              {entries.map((e) => (
                                <Box key={e.abbr} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}>
                                    {e.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    {e.label} ({e.value})
                                  </Typography>
                                </Box>
                              ))}
                              {entries.length > 0 && (
                                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                                  {entries.map((e) => (
                                    <Typography key={e.abbr} variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', mb: 0.25 }}>
                                      {e.abbr}: {e.tooltip}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          }
                          componentsProps={{
                            tooltip: {
                              sx: { maxWidth: 360, bgcolor: 'rgba(30,30,50,0.97)', p: 1.5 },
                            },
                          }}
                        >
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: 18,
                              color: isDark ? '#8899aa' : '#5f7082',
                              cursor: 'pointer',
                              '&:hover': { color: '#2979ff' },
                            }}
                          />
                        </Tooltip>
                      );
                    })()}
                    {editing && (
                      <Tooltip title="CVSS Calculator öffnen">
                        <IconButton
                          data-testid="btn-cvss-calculator"
                          onClick={() => setCvssDialogOpen(true)}
                          size="small"
                          sx={{
                            color: '#2979ff',
                            bgcolor: isDark ? '#2c3358' : alpha('#2979ff', 0.08),
                            '&:hover': { bgcolor: isDark ? '#1e3a5f' : alpha('#2979ff', 0.16) },
                            width: 28,
                            height: 28,
                          }}
                        >
                          <CalculateIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>

              {/* Schwachstellenklasse */}
              <TableRow>
                <TableCell sx={labelCellSx}>Schwachstellenklasse</TableCell>
                <TableCell sx={valueCellSx}>
                  {editing ? (
                    <Select
                      data-testid="field-schwachstellenklasse"
                      variant="standard"
                      size="small"
                      value={formData.schwachstellenklasse_id || ''}
                      onChange={(e) =>
                        handleSchwachstellenklasseChange(
                          e.target.value ? (e.target.value as number) : undefined
                        )
                      }
                      displayEmpty
                      disableUnderline
                      sx={{ fontSize: '0.9rem', '& .MuiSelect-select': { py: 0 }, minWidth: 200 }}
                    >
                      <MenuItem value="">
                        <em>Keine Auswahl</em>
                      </MenuItem>
                      {sortedSchwachstellenklassen.map((klasse) => (
                        <MenuItem key={klasse.id} value={klasse.id}>
                          {klasse.name_de}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    getKlasseName(formData.schwachstellenklasse_id) || '–'
                  )}
                </TableCell>
              </TableRow>

              {/* CWE */}
              <TableRow>
                <TableCell sx={labelCellSx}>CWE</TableCell>
                <TableCell sx={valueCellSx}>
                  {editing ? (
                    <Autocomplete
                      data-testid="field-cwe"
                      options={cweOptions}
                      getOptionLabel={(option) => `CWE-${option.id}: ${option.name}`}
                      value={selectedCwe}
                      onChange={(_event, newValue) => {
                        setSelectedCwe(newValue);
                        handleInputChange('cwe_id', newValue ? newValue.id : undefined);
                      }}
                      onInputChange={(_event, newInputValue) => {
                        setCweSearchText(newInputValue);
                      }}
                      loading={cweLoading}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      noOptionsText={cweSearchText ? 'Keine CWE gefunden' : 'CWE-ID oder Name eingeben...'}
                      size="small"
                      slotProps={{
                        paper: {
                          sx: {
                            bgcolor: isDark ? '#262b50' : 'background.paper',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                            boxShadow: isDark
                              ? '0 8px 24px rgba(0,0,0,0.5)'
                              : '0 8px 24px rgba(0,0,0,0.15)',
                            '& .MuiAutocomplete-option': {
                              '&:hover': {
                                bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                              },
                              '&[aria-selected="true"]': {
                                bgcolor: isDark ? 'rgba(43,168,98,0.18)' : 'rgba(43,168,98,0.12)',
                              },
                            },
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder="CWE-ID oder Name..."
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            endAdornment: (
                              <>
                                {cweLoading ? <AutocompleteLoading color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.9rem', p: 0 } }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              CWE-{option.id}: {option.name}
                            </Typography>
                            {option.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {option.description}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                    />
                  ) : (
                    selectedCwe
                      ? `CWE-${selectedCwe.id}: ${selectedCwe.name}`
                      : schwachstelle?.cwe
                        ? `CWE-${schwachstelle.cwe.id}: ${schwachstelle.cwe.name}`
                        : '–'
                  )}
                </TableCell>
              </TableRow>

              {/* Host */}
              <TableRow>
                <TableCell sx={labelCellSx}>Host</TableCell>
                <TableCell sx={valueCellSx}>
                  {editing ? (
                    <Autocomplete
                      freeSolo
                      options={hostSuggestions}
                      value={formData.host}
                      onChange={(_, newValue) => handleInputChange('host', newValue ?? '')}
                      inputValue={formData.host}
                      onInputChange={(_, newInputValue) => handleInputChange('host', newInputValue)}
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          data-testid="field-host"
                          variant="standard"
                          size="small"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                          }}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.9rem', p: 0 } }}
                        />
                      )}
                    />
                  ) : (
                    formData.host || '–'
                  )}
                </TableCell>
              </TableRow>

              {/* IP-Adresse */}
              <TableRow>
                <TableCell sx={labelCellSx}>IP-Adresse</TableCell>
                <TableCell sx={valueCellSx}>
                  {editing ? (
                    <Autocomplete
                      freeSolo
                      options={ipSuggestions}
                      value={formData.ip_adresse}
                      onChange={(_, newValue) => handleInputChange('ip_adresse', newValue ?? '')}
                      inputValue={formData.ip_adresse}
                      onInputChange={(_, newInputValue) => handleInputChange('ip_adresse', newInputValue)}
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          data-testid="field-ip"
                          variant="standard"
                          size="small"
                          fullWidth
                          error={Boolean(ipAdresseError)}
                          helperText={ipAdresseError || undefined}
                          placeholder="z. B. 10.0.0.1, 10.0.0.2"
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                          }}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.9rem', fontFamily: 'monospace', p: 0 } }}
                        />
                      )}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {formData.ip_adresse || '–'}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>

              {/* Risikoszenarien */}
              <TableRow>
                <TableCell sx={labelCellSx}>Risikoszenarien</TableCell>
                <TableCell sx={valueCellSx}>
                  {editing ? (
                    <Select
                      data-testid="field-risikoszenarien"
                      variant="standard"
                      size="small"
                      multiple
                      value={formData.risikoszenario_ids || []}
                      onChange={(e) =>
                        handleInputChange('risikoszenario_ids', e.target.value as number[])
                      }
                      disableUnderline
                      displayEmpty
                      renderValue={(selected) => {
                        const sel = selected as number[];
                        if (sel.length === 0) return <em style={{ opacity: 0.5 }}>Auswahl...</em>;
                        return (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {sel.map((value) => {
                              const rs = sortedRisikoszenarien.find((r) => r.id === value);
                              return <Chip key={value} label={rs ? rs.name_de : value} size="small" variant="outlined" />;
                            })}
                          </Box>
                        );
                      }}
                      sx={{ fontSize: '0.9rem', '& .MuiSelect-select': { py: 0 }, minWidth: 200 }}
                    >
                      {sortedRisikoszenarien.map((rs) => (
                        <MenuItem key={rs.id} value={rs.id}>
                          <Checkbox checked={(formData.risikoszenario_ids || []).indexOf(rs.id) > -1} size="small" />
                          <ListItemText primary={rs.name_de} />
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (formData.risikoszenario_ids || []).length > 0 ? (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {getRisikoszenarioNames(formData.risikoszenario_ids).map((name, i) => (
                        <Chip key={i} label={name} size="small" variant="outlined" />
                      ))}
                    </Box>
                  ) : (
                    '–'
                  )}
                </TableCell>
              </TableRow>

              {/* Tools */}
              <TableRow>
                <TableCell sx={labelCellSx}>Tools</TableCell>
                <TableCell sx={valueCellSx}>
                  {editing ? (
                    <Select
                      data-testid="field-tools"
                      variant="standard"
                      size="small"
                      multiple
                      value={formData.tool_ids || []}
                      onChange={(e) =>
                        handleInputChange('tool_ids', e.target.value as number[])
                      }
                      disableUnderline
                      displayEmpty
                      renderValue={(selected) => {
                        const sel = selected as number[];
                        if (sel.length === 0) return <em style={{ opacity: 0.5 }}>Auswahl...</em>;
                        return (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {sel.map((value) => {
                              const tool = sortedTools.find((t) => t.id === value);
                              return <Chip key={value} label={tool ? tool.name : value} size="small" variant="outlined" />;
                            })}
                          </Box>
                        );
                      }}
                      sx={{ fontSize: '0.9rem', '& .MuiSelect-select': { py: 0 }, minWidth: 200 }}
                    >
                      {sortedTools.map((tool) => (
                        <MenuItem key={tool.id} value={tool.id}>
                          <Checkbox checked={(formData.tool_ids || []).indexOf(tool.id) > -1} size="small" />
                          <ListItemText primary={tool.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (formData.tool_ids || []).length > 0 ? (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {getToolNames(formData.tool_ids).map((name, i) => (
                        <Chip key={i} label={name} size="small" variant="outlined" />
                      ))}
                    </Box>
                  ) : (
                    '–'
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Bounty-Verteilung ── */}
      {!isNew && schwachstelleId && (
        <BountyVerteilung schwachstelleId={schwachstelleId} editable={editing} reporter={schwachstelle?.reporter} />
      )}

      {/* ── Text Editors (always rendered, editable toggled) ── */}
      {/* overflow sichtbar: MUI-Card default hidden bricht position:sticky der Editor-Toolbar */}
      <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent>
          {editing && formData.schwachstellenklasse_id && (() => {
            const klasse = schwachstellenklassen.find((k) => k.id === formData.schwachstellenklasse_id);
            if (!klasse) return null;
            const hasDe = !!klasse.allgemeine_beschreibung_de;
            const hasEn = !!klasse.allgemeine_beschreibung_en;
            if (!hasDe && !hasEn) return null;
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(25,118,210,0.04)',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(25,118,210,0.15)'}`,
                }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  Aus Klasse &ldquo;{klasse.name_de}&rdquo; einfügen:
                </Typography>
                {hasDe && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleInsertKlasseBeschreibung('de')}
                    sx={{ minWidth: 0, px: 1.5, py: 0.25, fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.4 }}
                  >
                    DE
                  </Button>
                )}
                {hasEn && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleInsertKlasseBeschreibung('en')}
                    sx={{ minWidth: 0, px: 1.5, py: 0.25, fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.4 }}
                  >
                    EN
                  </Button>
                )}
              </Box>
            );
          })()}
          <TipTapEditor
            ref={editing ? allgemeinEditorRef : undefined}
            testId="editor-allgemein"
            label="Allgemeine Beschreibung"
            content={formData.allgemeine_beschreibung || ''}
            onChange={editing ? (html) => handleInputChange('allgemeine_beschreibung', html) : undefined}
            editable={editing}
            placeholder={editing ? 'Allgemeine Beschreibung der Schwachstelle...' : undefined}
            minHeight={editing ? 220 : 80}
            stickyToolbarTop={tiptapStickyToolbarTop}
            onFocus={editing ? () => { activeEditorRef.current = allgemeinEditorRef.current; } : undefined}
            onInsertImageClick={editing ? () => { activeEditorRef.current = allgemeinEditorRef.current; } : undefined}
            galleryImages={galleryImages}
            onInsertImageFromGallery={editing ? handleInsertImage : undefined}
            onPasteImage={editing && schwachstelleId ? handlePasteImageUpload : undefined}
            onEditImage={editing && schwachstelleId ? handleEditImage : undefined}
            findingReferences={formData.audit_id ? findingReferences : undefined}
            findingReferenceLang={findingReferenceLang}
            excludeFindingId={schwachstelleId}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent>
          <TipTapEditor
            ref={editing ? detailEditorRef : undefined}
            testId="editor-detail"
            label="Detailbeschreibung"
            labelAction={canUseDetailbeschreibungAi ? (
              <Tooltip
                title={
                  !editing
                    ? 'Bitte zuerst in den Bearbeitungsmodus wechseln.'
                    : 'Startet ein Sprachinterview fuer einen ersten KI-Entwurf.'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SmartToyOutlinedIcon />}
                    onClick={() => setDetailAiDialogOpen(true)}
                    disabled={!editing}
                    sx={{ flexShrink: 0 }}
                  >
                    AI-Assistant
                  </Button>
                </span>
              </Tooltip>
            ) : undefined}
            content={formData.detailbeschreibung || ''}
            onChange={editing ? (html) => handleInputChange('detailbeschreibung', html) : undefined}
            editable={editing}
            placeholder={editing ? 'Detaillierte Beschreibung der Schwachstelle...' : undefined}
            minHeight={editing ? 'max(500px, calc(100vh - 360px))' : 80}
            stickyToolbarTop={tiptapStickyToolbarTop}
            onFocus={editing ? () => { activeEditorRef.current = detailEditorRef.current; } : undefined}
            onInsertImageClick={editing ? () => { activeEditorRef.current = detailEditorRef.current; } : undefined}
            galleryImages={galleryImages}
            onInsertImageFromGallery={editing ? handleInsertImage : undefined}
            onPasteImage={editing && schwachstelleId ? handlePasteImageUpload : undefined}
            onEditImage={editing && schwachstelleId ? handleEditImage : undefined}
            wordFindingLead={wordFindingLead}
            findingReferences={formData.audit_id ? findingReferences : undefined}
            findingReferenceLang={findingReferenceLang}
            excludeFindingId={schwachstelleId}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent>
          <TipTapEditor
            ref={editing ? mitigationEditorRef : undefined}
            testId="editor-mitigation"
            label="Mitigationsmassnahmen"
            content={formData.mitigationsmassnahmen || ''}
            onChange={editing ? (html) => handleInputChange('mitigationsmassnahmen', html) : undefined}
            editable={editing}
            placeholder={editing ? 'Empfohlene Mitigationsmassnahmen...' : undefined}
            minHeight={editing ? 220 : 80}
            stickyToolbarTop={tiptapStickyToolbarTop}
            onFocus={editing ? () => { activeEditorRef.current = mitigationEditorRef.current; } : undefined}
            onInsertImageClick={editing ? () => { activeEditorRef.current = mitigationEditorRef.current; } : undefined}
            galleryImages={galleryImages}
            onInsertImageFromGallery={editing ? handleInsertImage : undefined}
            onPasteImage={editing && schwachstelleId ? handlePasteImageUpload : undefined}
            onEditImage={editing && schwachstelleId ? handleEditImage : undefined}
            findingReferences={formData.audit_id ? findingReferences : undefined}
            findingReferenceLang={findingReferenceLang}
            excludeFindingId={schwachstelleId}
          />
        </CardContent>
      </Card>

      {/* ── Screenshots / Anhaenge (always visible, at the bottom) ── */}
      <ScreenshotGallery
        schwachstelleId={schwachstelleId}
        onInsertImage={editing ? handleInsertImage : undefined}
        onInsertCodeBlock={editing ? handleInsertCodeBlock : undefined}
        editable={editing}
        onFilesLoaded={handleScreenshotFilesLoaded}
        onEditImage={editing ? handleEditImage : undefined}
        refreshToken={galleryRefreshToken}
        referenceHtml={
          [
            formData.allgemeine_beschreibung,
            formData.detailbeschreibung,
            formData.mitigationsmassnahmen,
          ]
            .filter(Boolean)
            .join(' ') || ''
        }
      />

      <BurpRequestResponseSection
        schwachstelleId={schwachstelleId}
        editable={editing}
        onInsertCodeBlock={editing ? handleInsertBurpCodeBlock : undefined}
      />

      <ImageAnnotationEditor
        open={imageEditorState !== null}
        file={imageEditorState?.file ?? null}
        files={imageEditorState?.files ?? []}
        fileApi={filesService}
        canInsert={Boolean(imageEditorState?.canInsert)}
        initialWordSize={imageEditorState?.wordSize}
        onSaved={handleImageEditorSaved}
        onClose={() => finishImageEdit(null)}
      />

      {!isNew && schwachstelleId && (
        <KommentarSection
          schwachstelleId={schwachstelleId}
          refreshTrigger={kommentarRefresh}
        />
      )}

      {/* ── Dialogs ──────────────────────────────────────── */}
      {!isNew && id && (
        <HistorieTimeline
          entitaetTyp="Schwachstelle"
          entitaetId={parseInt(id)}
          open={historieOpen}
          onClose={() => setHistorieOpen(false)}
        />
      )}

      {!isNew && id && (
        <VersionsHistorie
          type="schwachstelle"
          entityId={parseInt(id)}
          open={versionenOpen}
          onClose={() => setVersionenOpen(false)}
          onRestore={() => window.location.reload()}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Schwachstelle löschen"
        message="Möchten Sie diese Schwachstelle wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      <CvssCalculator
        open={cvssDialogOpen}
        onClose={() => setCvssDialogOpen(false)}
        onApply={handleCvssApply}
        initialVector={formData.cvss31_vector}
      />

      <Dialog
        open={rejectDialogOpen}
        onClose={() => { setRejectDialogOpen(false); setRejectComment(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schwachstelle ablehnen</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Bitte geben Sie eine Begründung für die Ablehnung an. Dieser Kommentar wird in den Kommentaren der Schwachstelle angezeigt.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            label="Begründung (Pflichtfeld)"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectDialogOpen(false); setRejectComment(''); }}>
            Abbrechen
          </Button>
          <Button
            onClick={handleRejectConfirm}
            color="error"
            variant="contained"
            disabled={!rejectComment.trim() || reviewing}
          >
            {reviewing ? <CircularProgress size={20} /> : 'Ablehnen'}
          </Button>
        </DialogActions>
      </Dialog>

      {canUseDetailbeschreibungAi && (
        <DetailbeschreibungAIAssistantDialog
          open={detailAiDialogOpen}
          context={detailbeschreibungAiContext}
          existingContent={formData.detailbeschreibung || ''}
          onClose={() => setDetailAiDialogOpen(false)}
          onApplyDraft={(html) => {
            handleInputChange('detailbeschreibung', html);
            showSuccess('KI-Entwurf in die Detailbeschreibung uebernommen');
          }}
        />
      )}
    </Box>
  );
};

export default SchwachstelleDetailPage;
